import { useQueryClient } from '@tanstack/react-query';
import { Form, Space, Typography } from 'antd';
import { FormInstance } from 'antd/lib';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { DurationWarnings } from '@/components/booking';
import { useOriginalResourceBookingDuration } from '@/hooks';
import { queryKeys } from '@/lib/react-query';
import { calculateDurationInMinutes, convertMinutesToHours, showMessage } from '@/utils';
import { useResourceCloseBooking } from '@/web-api/bookings';

const { Paragraph, Text } = Typography;

type ConfirmTimeProps = {
  bookingId: string;
  form: FormInstance<any>;
};

export function ConfirmTime({ bookingId, form }: ConfirmTimeProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [startDateValue, setStartDateValue] = useState('');
  const [startTimeValue, setStartTimeValue] = useState('');
  const [endDateValue, setEndDateValue] = useState('');
  const [endTimeValue, setEndTimeValue] = useState('');
  const [calculatedDuration, setCalculatedDuration] = useState<number | null>(null);

  const { originalDuration } = useOriginalResourceBookingDuration(bookingId);
  const closeBookingMutation = useResourceCloseBooking();

  // Calculate duration when times change
  useEffect(() => {
    const duration = calculateDurationInMinutes(
      startDateValue,
      startTimeValue,
      endDateValue,
      endTimeValue
    );
    setCalculatedDuration(duration);
  }, [startDateValue, startTimeValue, endDateValue, endTimeValue]);

  const onConfirmBooking = () => {
    const durationMinutes = calculateDurationInMinutes(
      startDateValue,
      startTimeValue,
      endDateValue,
      endTimeValue
    );

    if (!durationMinutes || durationMinutes <= 0) {
      return;
    }

    // Submit the booking directly (warnings are already shown in the form)
    submitBooking(durationMinutes);
  };

  const submitBooking = (durationMinutes: number) => {
    const combinedStartDateTimeValue = `${startDateValue}T${startTimeValue}`;
    closeBookingMutation.mutate(
      {
        bookingId,
        value: durationMinutes,
        startTime: dayjs(combinedStartDateTimeValue).format(),
      },
      {
        onSuccess: () => {
          showMessage('success', t('common:CONFIRMED'));
          void queryClient.invalidateQueries({ queryKey: queryKeys.resourceBookings.all });
          void navigate(`/app/mybookings`);
        },
      }
    );
  };

  // Derived values for display
  const hours = calculatedDuration ? convertMinutesToHours(calculatedDuration) : null;
  const originalHours = originalDuration ? convertMinutesToHours(originalDuration) : null;

  return (
    <Form
      form={form}
      layout='vertical'
      requiredMark='optional'
      initialValues={{ date: startDateValue, time: startTimeValue }}
      onFinish={onConfirmBooking}
    >
      <Paragraph>{t('common:confirmBookingText')}</Paragraph>
      <Space orientation={'vertical'} style={{ width: '100%' }}>
        <Text strong>{t('forms:startTime')}:</Text>
        <Space>
          <Form.Item name='startDate' noStyle>
            <input
              aria-label={t('forms:startTime') + ' ' + t('common:today')}
              type='date'
              onChange={(e) => setStartDateValue(e.target.value)}
            />
          </Form.Item>
          <Form.Item name='startTime' noStyle>
            <input
              aria-label={t('forms:startTime')}
              type='time'
              onChange={(e) => setStartTimeValue(e.target.value)}
            />
          </Form.Item>
        </Space>
        <Text strong>{t('forms:endTime')}:</Text>
        <Space>
          <Form.Item name='endDate' noStyle>
            <input
              aria-label={t('forms:endTime') + ' ' + t('common:today')}
              type='date'
              onChange={(e) => setEndDateValue(e.target.value)}
            />
          </Form.Item>
          <Form.Item name='endTime' noStyle>
            <input
              aria-label={t('forms:endTime')}
              type='time'
              onChange={(e) => setEndTimeValue(e.target.value)}
            />
          </Form.Item>
        </Space>
        {calculatedDuration && calculatedDuration > 0 && (
          <>
            <Text strong>
              {t('forms:duration')}: {t('common:minuteWithCount', { count: calculatedDuration })}
              {hours !== null && ` (${t('common:hourWithCount', { count: hours })})`}
            </Text>
            {originalDuration && (
              <Text type='secondary'>
                {t('common:originallyBooked')}:{' '}
                {t('common:minuteWithCount', { count: originalDuration })}
                {originalHours !== null &&
                  ` (${t('common:hourWithCount', { count: originalHours })})`}
              </Text>
            )}

            <DurationWarnings
              confirmedDuration={calculatedDuration}
              originalDuration={originalDuration}
            />
          </>
        )}
      </Space>
    </Form>
  );
}
